import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  login,
  loginByCode,
  logout,
} from "../actions/authActions";
import userService from "../../infrastructure/api/user";
import { markMenuLoaded, resetMenuCache } from "../../infrastructure/utils/menuSession";
import { applyProjectWorkspaceMenuLinks, applyMarketGrowthMenuLinks } from "../../presentation/pages/SLN/shared/projectWorkspace/projectWorkspaceMenu";
import { setLoading } from "./ui";

const initialState = {
  menu: [],
  selectedMenu: null,
  menuStatus: "idle",
};

const MAX_MENU_DEPTH = 12;

const cloneMenuNodes = (nodes) => {
  if (!Array.isArray(nodes)) {
    return [];
  }
  try {
    return JSON.parse(JSON.stringify(nodes));
  } catch {
    return [];
  }
};

const onMakeMenuLink = (parent, child, depth = 0) => {
  if (!Array.isArray(child) || depth > MAX_MENU_DEPTH) {
    return child;
  }

  child.forEach((menu) => {
    menu.oldLink = menu.link;
    if (menu.level === 0) {
      menu.link = `/${String(menu.link || "").replace(/^\/+/, "")}`;
    } else if (menu.level === 2 && !menu.link) {
      menu.link = parent.link;
    } else {
      menu.link = `${parent.link}/${String(menu.link || "").replace(/^\/+/, "")}`;
    }

    if (Array.isArray(menu.child)) {
      onMakeMenuLink(menu, menu.child, depth + 1);
    }
  });

  return child;
};

export const setUserMenuList = createAsyncThunk(
  "user/menu",
  async (arg, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const res = await userService.getMenuData();
      const rawMenu = cloneMenuNodes(Array.isArray(res.data) ? res.data : []);
      const menulist = applyMarketGrowthMenuLinks(
        applyProjectWorkspaceMenuLinks(
          onMakeMenuLink({ link: "" }, rawMenu)
        )
      );
      return { menu: menulist };
    } catch (error) {
      return rejectWithValue(error?.message || "menu-load-failed");
    } finally {
      dispatch(setLoading(false));
    }
  },
  {
    condition: (_, { getState }) => getState().user.menuStatus !== "loading",
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setSelectedMenu: (state, action) => {
      return { selectedMenu: action.payload };
    },
  },
  extraReducers: {
    [login.fulfilled]: (state) => {
      state.menu = [];
      state.selectedMenu = null;
      state.menuStatus = "idle";
    },
    [loginByCode.fulfilled]: (state) => {
      state.menu = [];
      state.selectedMenu = null;
      state.menuStatus = "idle";
    },
    [logout.fulfilled]: (state) => {
      state.menu = [];
      state.selectedMenu = null;
      state.menuStatus = "idle";
    },
    [setUserMenuList.pending]: (state) => {
      state.menuStatus = "loading";
    },
    [setUserMenuList.fulfilled]: (state, action) => {
      state.menu = Array.isArray(action.payload.menu) ? action.payload.menu : [];
      state.menuStatus = "loaded";
      markMenuLoaded();
    },
    [setUserMenuList.rejected]: (state, action) => {
      state.menu = [];
      state.menuStatus = "error";
      resetMenuCache();
    },
  },
});

const { reducer, actions } = userSlice;

export const { setSelectedMenu } = actions;
export default reducer;
